import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE } from './api';
import { Box, Button, Chip, Typography } from '@mui/material';

// ---------- Helper: rilevamento robusto tipo account ----------
async function detectAccountType(token) {
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  // 1) Provo come FREELANCE
  try {
    const r = await fetch(`${API_BASE}/freelancers/self`, { headers });
    if (r.ok) {
      const u = await r.json().catch(() => ({}));
      const isFreelance =
        u?.isFreelance === true ||
        String(u?.user_type || '').toLowerCase().startsWith('freelanc');
      if (isFreelance) return 'freelance';
      return 'production';
    }
  } catch { /* ignore */ }

  // 2) Provo come CASA DI PRODUZIONE
  try {
    const r2 = await fetch(`${API_BASE}/production-houses/self`, { headers });
    if (r2.ok) return 'production';
  } catch { /* ignore */ }

  return 'production';
}

export default function AlertsBoard({ token }) {
  const [accountType, setAccountType] = useState('production');
  const isProduction = accountType === 'production';

  const [alerts, setAlerts] = useState([]);
  const [tgAlerts, setTgAlerts] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    let abort = false;
    (async () => {
      const kind = await detectAccountType(token);
      if (!abort) setAccountType(kind);
    })();
    return () => { abort = true; };
  }, [token]);

  useEffect(() => {
    if (isProduction) return;
    let abort = false;
    (async () => {
      try {
        setError('');
        const res = await fetch(`${API_BASE}/alerts`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json().catch(() => []);
        if (!abort) setAlerts(Array.isArray(data) ? data : []);
      } catch {
        if (!abort) setError('Impossibile caricare gli alert.');
      }
    })();
    return () => { abort = true; };
  }, [token, isProduction]);

  useEffect(() => {
    if (isProduction) return;
    let abort = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/telegram/alerts`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json().catch(() => []);
        if (!abort) setTgAlerts(Array.isArray(data) ? data : []);
      } catch { /* ignore */ }
    })();
    return () => { abort = true; };
  }, [token, isProduction]);

  const allAlerts = useMemo(() => {
    if (isProduction) return [];
    return [...alerts, ...tgAlerts].filter(a => {
      const titolo = (a.titolo || a.title || '').toLowerCase();
      const descrizione = (a.descrizione || a.description || a.text || '').toLowerCase();
      return !(
        titolo.includes('operatore per spot') ||
        descrizione.includes('manual / telegram')
      );
    });
  }, [alerts, tgAlerts, isProduction]);

  if (isProduction) return null;

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Typography variant="h5">Alert Produzioni per te</Typography>
      {error && <Typography color="error">{error}</Typography>}

      {allAlerts.map((a, i) => (
        <Box key={i} sx={{ p: 2, borderRadius: 2, border: '1px solid #e5e7eb' }}>
          <Typography variant="h6" sx={{ mb: .5 }}>
            {a.titolo || a.title || a.topic || 'Annuncio'}
          </Typography>
          <Typography sx={{ mb: 1 }}>
            {a.descrizione || a.description || a.text || ''}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            {a.tag && <Chip size="small" label={a.tag} />}
            {a.ruolo && <Chip size="small" label={a.ruolo} />}
            {a.azienda && <Chip size="small" label={a.azienda} />}
            {a.produzione && <Chip size="small" label={a.produzione} />}
            {a.source === 'telegram' && <Chip size="small" label="Telegram" />}
          </Box>
          <Button variant="contained">Contatta</Button>
        </Box>
      ))}

      {(!allAlerts || allAlerts.length === 0) && !error && (
        <Typography sx={{ color: 'text.secondary' }}>Nessun alert al momento.</Typography>
      )}
    </Box>
  );
}
